import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getDisciplineSystem } from '../services/disciplineService';
import { getGradingSystem } from '../services/gradingService';
import { normalizeDisciplineSystem } from '../utils/gradingUtils';

const GradingSystemContext = createContext(null);

export function GradingSystemProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [gradingSystem, setGradingSystem] = useState(null);
  const [disciplineSystem, setDisciplineSystem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setGradingSystem(null);
      setDisciplineSystem(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    let isMounted = true;

    async function loadRatingSystems() {
      setIsLoading(true);
      setError(null);

      const [gradingResult, disciplineResult] = await Promise.allSettled([
        getGradingSystem(),
        getDisciplineSystem(),
      ]);

      if (!isMounted) {
        return;
      }

      if (gradingResult.status === 'fulfilled') {
        setGradingSystem(gradingResult.value);
      } else {
        setGradingSystem(null);
      }

      if (disciplineResult.status === 'fulfilled') {
        setDisciplineSystem(normalizeDisciplineSystem(disciplineResult.value));
      } else {
        setDisciplineSystem(null);
      }

      const gradingError = gradingResult.status === 'rejected' ? gradingResult.reason : null;
      const disciplineError = disciplineResult.status === 'rejected' ? disciplineResult.reason : null;

      if (gradingError && disciplineError) {
        setError(
          disciplineError.message
            || gradingError.message
            || 'امکان دریافت سیستم نمره‌دهی وجود ندارد.'
        );
      } else if (gradingError) {
        setError(gradingError.message || 'امکان دریافت سیستم نمره‌دهی آموزشی وجود ندارد.');
      } else if (disciplineError) {
        setError(disciplineError.message || 'امکان دریافت سیستم نمره‌دهی شایستگی های عمومی وجود ندارد.');
      }

      setIsLoading(false);
    }

    loadRatingSystems();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({
      gradingSystem,
      disciplineSystem,
      isLoading,
      error,
    }),
    [gradingSystem, disciplineSystem, isLoading, error]
  );

  return <GradingSystemContext.Provider value={value}>{children}</GradingSystemContext.Provider>;
}

export function useGradingSystem() {
  const context = useContext(GradingSystemContext);

  if (!context) {
    throw new Error('useGradingSystem must be used inside GradingSystemProvider');
  }

  return context;
}
